import { useCallback, useContext } from "react";
import { ModalResultType } from "../components/AppModal/ModalTypes";
import ModalContext from "../components/AppModal/ModalContext";

const defaultOptions = {
    title: undefined,
    subtitle: undefined,
    details: null,
    okLabel: "Okay",
    cancelLabel: "Cancel",
    closeLabel: "Close",
    showOk: true,
    showCancel: true,
    showClose: true,
    onOk: () => true,
    onCancel: () => true,
    onClose: () => true,
};

class ModalState {
    constructor(id, modalType, options, onOk, onCancel, onClose, data) {
        this.id = id;
        this.modalType = modalType;
        this.options = options || defaultOptions;
        this.onOk = onOk;
        this.onCancel = onCancel;
        this.onClose = onClose;
        this.data = data;
        this.isOpen = false;
    }
    onOk() {}
    onCancel() {}
    onClose() {}
}

const useAppModalService = () => {
    const { modalStack, setModalStack } = useContext(ModalContext);

    // Use functional updates to avoid stale closures & render loops
    const closeModal = useCallback(
        (id) => {
            setModalStack((prev) => prev.filter((m) => m.id !== id));
        },
        [setModalStack]
    );

    const closeAll = useCallback(() => {
        setModalStack(() => []); // explicit to emphasize no dependency on prev
    }, [setModalStack]);

    const openModal = useCallback(
        async (id, type, options = {}, data) => {
            // Fast duplicate guard against same-id reopens within the same tick
            if (modalStack.some((m) => m.id === id)) {
                throw new Error(`A modal with the same id '${id}' is already present`);
            }

            return new Promise((resolve) => {
                const opts = { ...defaultOptions, ...options };

                const modal = new ModalState(
                    id,
                    type,
                    {
                        title: opts.title,
                        subTitle: opts.subtitle, // note: your prop is 'subtitle'; keeping your original mapping
                        details: opts.details,
                        okLabel: opts.okLabel,
                        cancelLabel: opts.cancelLabel,
                        showOkBtn: opts.showOk,
                        showCloseBtn: opts.showClose,
                        showCancelBtn: opts.showCancel,
                        modalComponent: opts.modalComponent,
                    },
                    new Proxy(opts.onOk, {
                        apply: async (target, _, args) => {
                            closeModal(id);
                            return resolve({
                                result: ModalResultType.OK,
                                payload: await target(...args),
                            });
                        },
                    }),
                    new Proxy(opts.onCancel, {
                        apply: async (target, _, args) => {
                            closeModal(id);
                            return resolve({
                                result: ModalResultType.CANCEL,
                                payload: await target(...args),
                            });
                        },
                    }),
                    new Proxy(opts.onClose, {
                        apply: async (target, _, args) => {
                            closeModal(id);
                            return resolve({
                                result: ModalResultType.CLOSE,
                                payload: await target(...args),
                            });
                        },
                    }),
                    data
                );

                modal.isOpen = true;

                // Functional add to stack (no dependency on current render’s modalStack)
                setModalStack((prev) => [...prev, modal]);
            });
        },
        [modalStack, setModalStack, closeModal]
    );

    return {
        /** behaviour **/
        openModal,
        closeModal,
        closeAll, // stable reference (useCallback)

        /** state **/
        modalStack,
        stackSize: modalStack.length,
    };
};

export default useAppModalService;
